import { useEffect, useRef } from 'react';
import { loyaltyHtmlTemplate } from '../../loyaltyPreLogin/htmlTemplate';
import '../../assets/css/loyalty-styles.scss';

const LoyaltyStaticPage = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Preload and optimize images
    const preloadImages = async () => {
      const tierImages = [
        './assets/images/FASFAS_Tiers-1.webp',
        './assets/images/FASFAS_Tiers-2.webp',
        './assets/images/FASFAS_Tiers-3.webp',
        './assets/images/FASFAS_Tiers-4.webp',
        './assets/images/FASFAS_Tiers-5.webp'
      ];

      // Create optimized loading for small tier images
      const loadOptimizedImage = (src, maxWidth = 48) => {
        return new Promise((resolve) => {
          const img = new Image();
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          img.onload = () => {
            const aspectRatio = img.height / img.width;
            canvas.width = maxWidth;
            canvas.height = maxWidth * aspectRatio;

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              const optimizedUrl = URL.createObjectURL(blob);
              resolve({ original: src, optimized: optimizedUrl, img });
            }, 'image/webp', 0.8);
          };

          img.src = src;
        });
      };

      // Load all tier images with optimization
      const imagePromises = tierImages.map(src => loadOptimizedImage(src));
      const loadedImages = await Promise.all(imagePromises);

      return loadedImages;
    };

    const initializeComponents = async () => {
      try {
        const optimizedImages = await preloadImages();
        // Replace small tier images with optimized versions
        if (containerRef.current) {
          const tierItems = containerRef.current.querySelectorAll('.tier_item img');
          tierItems.forEach((img, index) => {
            if (optimizedImages[index]) {
              img.src = optimizedImages[index].optimized;
              img.style.objectFit = 'contain';
              img.style.width = '100%';
              img.style.height = '100%';
            }
          });

          const flipCardImages = containerRef.current.querySelectorAll('.flip-card img');
          flipCardImages.forEach((img, index) => {
            if (optimizedImages[index]) {
              img.src = optimizedImages[index].optimized;
              const fullImg = new Image();
              fullImg.onload = () => {
                img.src = optimizedImages[index].original;
              };
              fullImg.src = optimizedImages[index].original;
            }
          });
        }
        const { Splide } = await import('@splidejs/splide');
        const splide = new Splide('.splide-elite-rewards', {
          type: 'slide',
          gap: '1rem',
          pagination: false,
          lazyLoad: true,
          preloadPages: 1,
        }).mount();

        const prevBtn = document.getElementById('prev-slide');
        const nextBtn = document.getElementById('next-slide');

        prevBtn?.addEventListener('click', () => splide.go('<'));
        nextBtn?.addEventListener('click', () => splide.go('>'));

        // Pagination with optimized updates
        const paginationButtons = document.querySelectorAll('#customPagination button');
        const activeClasses = [
          'bg-gradient-to-r from-emerald-200 to-gray-500',
          'bg-gradient-to-r from-green-400 to-green-600',
          'bg-gradient-to-r from-blue-400 to-blue-600',
          'bg-gradient-to-r from-purple-400 to-purple-600',
          'bg-gradient-to-r from-yellow-400 to-orange-500'
        ];

        // Efficient pagination handling
        const updatePagination = (activeIndex) => {
          paginationButtons.forEach((btn, index) => {
            btn.className = 'w-3 h-3 rounded-full transition-all duration-200';
            if (index === activeIndex) {
              btn.className += ' ' + activeClasses[activeIndex];
            } else {
              btn.className += ' bg-gray-300 hover:bg-gray-400';
            }
          });
        };

        paginationButtons.forEach((btn, index) => {
          btn.addEventListener('click', () => {
            splide.go(index);
            updatePagination(index);
          });
        });

        splide.on('move', (newIndex) => {
          const realIndex = splide.Components.Slides.getAt(newIndex).index;
          updatePagination(realIndex);
        });

        // Initialize first pagination state
        updatePagination(0);

        // Optimized flip card handling
        const handleFlipCardClick = (event) => {
          const flipCard = event.target.closest('.flip-card');
          if (flipCard && !flipCard.dataset.flipping) {
            flipCard.dataset.flipping = 'true';
            flipCard.classList.toggle('rotate-y-180');

            // Reset flipping flag after animation
            setTimeout(() => {
              delete flipCard.dataset.flipping;
            }, 700);
          }
        };

        containerRef.current?.addEventListener('click', handleFlipCardClick);

        return () => {
          optimizedImages.forEach(({ optimized }) => {
            URL.revokeObjectURL(optimized);
          });
        };

      } catch (error) {
        console.error('Error initializing:', error);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initializeComponents, { timeout: 1000 });
    } else {
      setTimeout(initializeComponents, 100);
    }

  }, []);

  useEffect(() => {
    // Find the target div inside the body
    const targetDiv = document.querySelector('.SB-Lite');

    if (targetDiv) {
      // Add your specific class
      targetDiv.classList.add('SB-loyalty-promoPage');

      // Cleanup function to remove class when component unmounts
      return () => {
        targetDiv.classList.remove('SB-loyalty-promoPage');
      };
    }
  }, []);

  return (
    <div
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: loyaltyHtmlTemplate }}
    />
  );
};

export default LoyaltyStaticPage;